import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import i18next, { hasLocaleCatalog, loadLocaleCatalog } from './index';
import { DEFAULT_LOCALE, getLanguage, getLanguages, matchLocale } from './registry';
import { appUrl, localeFromPath, localizedPath, normalizeLocale, SUPPORTED_LOCALES } from './routes';

const LocaleContext = createContext(null);
const readPreference = key => {
  try { return window.localStorage.getItem(key); } catch { return null; }
};
const savePreference = (key, value) => {
  try { window.localStorage.setItem(key, value); } catch { /* Private browsing can disable storage. */ }
};

export function detectLocale(preferences) {
  return preferences.map(value => matchLocale(value, 'marketing')).find(Boolean) || DEFAULT_LOCALE;
}

export function entryPath(pathname, preferences) {
  // Shared localized links stay authoritative. Detect only on unprefixed URLs;
  // localizedPath also leaves untranslated content such as the blog untouched.
  const prefix = pathname.split('/')[1]?.toLowerCase();
  if (SUPPORTED_LOCALES.some(code => code.toLowerCase() === prefix)) return pathname;
  return localizedPath(pathname, detectLocale(preferences));
}

export function LocaleProvider({ children }) {
  const location = useLocation();
  const [entry] = useState(() => ({
    key: location.key,
    pathname: entryPath(location.pathname, [
      readPreference('noteician.locale'), ...(navigator.languages || [navigator.language]),
    ]),
  }));
  // Resolve the arrival before rendering page copy or pricing. Run once per
  // visit so navigation and a deliberate language switch never bounce back.
  if (location.key === entry.key && location.pathname !== entry.pathname) {
    return <Navigate replace to={`${entry.pathname}${location.search}${location.hash}`} />;
  }
  return <ResolvedLocaleProvider>{children}</ResolvedLocaleProvider>;
}

function ResolvedLocaleProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Detection has resolved the entry URL; it now determines language and currency.
  const locale = localeFromPath(location.pathname);
  const language = getLanguage(locale, 'marketing');
  const [loaded, setLoaded] = useState(() => hasLocaleCatalog(locale) ? locale : null);
  const [loadError, setLoadError] = useState(null);
  const [retry, setRetry] = useState(0);
  const currency = language.suggestedCurrency === 'eur' ? 'eur' : 'gbp';

  useEffect(() => {
    let active = true;
    setLoadError(null);
    loadLocaleCatalog(locale).then(() => {
      if (!active) return;
      // All resources exist before rendering a page, so a failed chunk cannot
      // silently publish English copy with another language's metadata.
      i18next.changeLanguage(locale);
      document.documentElement.lang = locale;
      document.documentElement.dir = language.direction;
      setLoaded(locale);
    }).catch(() => { if (active) setLoadError(locale); });
    return () => { active = false; };
  }, [locale, language.direction, retry]);

  const value = useMemo(() => ({
    locale,
    language,
    languages: getLanguages('marketing'),
    currency,
    setLocale: next => {
      const normalized = normalizeLocale(next);
      savePreference('noteician.locale', normalized);
      navigate(`${localizedPath(location.pathname, normalized)}${location.search}${location.hash}`);
    },
    path: path => localizedPath(path, locale),
    appLink: (options = {}) => appUrl({ locale, currency, ...options }),
  }), [locale, language, currency, location.pathname, location.search, location.hash, navigate]);

  if (loaded !== locale) {
    return <main lang="en" dir="ltr" className="locale-status" aria-live="polite">
      <Helmet><html lang="en" dir="ltr" /><title>Noteician</title><meta name="robots" content="noindex,nofollow" /></Helmet>
      {loadError === locale ? <>
        <p role="alert">Unable to load <bdi>{language.nativeName}</bdi>.</p>
        <button type="button" onClick={() => { setLoadError(null); setRetry(value => value + 1); }}>Retry</button>
        {locale !== DEFAULT_LOCALE && <a href={localizedPath(location.pathname, DEFAULT_LOCALE)}
          onClick={event => { event.preventDefault(); value.setLocale(DEFAULT_LOCALE); }}>English</a>}
      </> : <p role="status"><bdi lang={locale}>{language.nativeName}</bdi>…</p>}
    </main>;
  }

  return <I18nextProvider i18n={i18next}><LocaleContext.Provider value={value}>{children}</LocaleContext.Provider></I18nextProvider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  const { t } = useTranslation('translation', { lng: context?.locale || DEFAULT_LOCALE });
  return { ...context, t };
}
